import BlogLayout from '@/pages/blog/components/BlogLayout';
import Breadcrumbs from '@/pages/blog/components/Breadcrumbs';
import TableOfContents from '@/pages/blog/components/TableOfContents';
import AuthorBio from '@/pages/blog/components/AuthorBio';
import RelatedArticles from '@/pages/blog/components/RelatedArticles';

const tocItems = [
  { id: 'intro', label: 'Exercise After a Heart Attack: The Basics' },
  { id: 'why-exercise', label: 'Why Exercise Is Critical After a Heart Attack' },
  { id: 'cardiac-rehab', label: 'Cardiac Rehabilitation: Your First Step' },
  { id: 'timeline', label: 'When Can You Start Exercising?' },
  { id: 'safe-exercises', label: 'Safe Exercises After a Heart Attack' },
  { id: 'exercises-avoid', label: 'Exercises to Avoid (At First)' },
  { id: 'warning-signs', label: 'Warning Signs to Watch For' },
  { id: 'long-term', label: 'Building a Long-Term Exercise Habit' },
  { id: 'faq', label: 'Frequently Asked Questions' },
];

const relatedArticles = [
  { slug: 'exercise-with-high-blood-pressure-over-60', title: 'Exercising with High Blood Pressure Over 60: Do\'s and Don\'ts', category: 'Health Conditions' },
  { slug: 'resistance-training-for-seniors', title: 'Why Resistance Training is the #1 Priority for Men Over 60', category: 'Strength Training' },
  { slug: 'weight-training-over-60-safety-tips', title: 'Weight Training Over 60: 10 Safety Tips Every Man Needs to Know', category: 'Strength Training' },
];

const schema = JSON.stringify([
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Exercise After a Heart Attack: A Safe Guide for Men Over 60',
    author: { '@type': 'Person', name: 'Mason Hale', url: 'https://masonhalefit.com' },
    publisher: { '@type': 'Organization', name: 'Mason Hale Fitness', url: 'https://masonhalefit.com' },
    datePublished: '2024-02-10',
    dateModified: '2024-04-06',
    description: 'Safe exercise guidelines for men over 60 after a heart attack. Learn when to start, what exercises are safe, and how to build back your fitness safely.',
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://masonhalefit.com/blog/exercise-after-heart-attack-over-60' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'When can I exercise after a heart attack?',
        acceptedAnswer: { '@type': 'Answer', text: 'Most patients can begin supervised cardiac rehabilitation within 1-2 weeks of a heart attack, and light walking even sooner. The timeline depends on the severity of the event and your cardiologist\'s assessment. Never start an exercise program after a heart attack without medical clearance.' },
      },
      {
        '@type': 'Question',
        name: 'Is strength training safe after a heart attack?',
        acceptedAnswer: { '@type': 'Answer', text: 'Yes, with proper medical clearance and appropriate progression. Light to moderate resistance training is actually recommended as part of cardiac rehabilitation. The key is starting very light, avoiding breath-holding (Valsalva maneuver), and progressing gradually under medical supervision.' },
      },
      {
        '@type': 'Question',
        name: 'What exercises should I avoid after a heart attack?',
        acceptedAnswer: { '@type': 'Answer', text: 'In the early recovery phase, avoid high-intensity exercise, heavy lifting, exercises that involve breath-holding, and activities that cause chest pain or significant shortness of breath. As you progress through cardiac rehab, these restrictions are gradually lifted based on your individual recovery.' },
      },
      {
        '@type': 'Question',
        name: 'Can exercise prevent another heart attack?',
        acceptedAnswer: { '@type': 'Answer', text: 'Yes. Regular exercise is one of the most powerful tools for secondary prevention of heart attacks. Studies show that cardiac rehabilitation and regular exercise reduce the risk of a second heart attack by 20-30% and significantly improve survival rates.' },
      },
    ],
  },
]);

export default function ExerciseAfterHeartAttackPage() {
  return (
    <BlogLayout
      title="Exercise After a Heart Attack: Safe Guide for Men Over 60"
      description="Safe exercise guidelines for men over 60 after a heart attack. When to start, what's safe, and how to rebuild your fitness with your doctor's support."
      keywords="exercise after heart attack over 60, cardiac rehab exercise, safe exercise after heart attack, men over 60 heart health"
      canonicalPath="/blog/exercise-after-heart-attack-over-60"
      datePublished="2024-02-10"
      dateModified="2024-04-06"
      schemaMarkup={schema}
    >
      <Breadcrumbs title="Exercise After a Heart Attack: A Safe Guide for Men Over 60" />

      <div className="mb-6">
        <span className="text-[#c8a96e] text-xs font-bold tracking-[0.2em] uppercase">Health Conditions</span>
      </div>

      <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-8">
        <p className="text-red-300 text-sm font-bold mb-1">Medical Disclaimer</p>
        <p className="text-red-200/70 text-xs leading-relaxed">This article is for informational purposes only and does not constitute medical advice. Always consult your cardiologist or physician before starting any exercise program after a heart attack. Your individual situation requires personalized medical guidance.</p>
      </div>

      <h1 className="text-white text-3xl sm:text-4xl font-black leading-tight mb-4">
        Exercise After a Heart Attack: A Safe Guide for Men Over 60
      </h1>

      <p className="text-gray-400 text-sm mb-8">
        By <strong className="text-white">Mason Hale</strong> &nbsp;·&nbsp; February 10, 2024 &nbsp;·&nbsp; 11 min read
      </p>

      <TableOfContents items={tocItems} />

      <section id="intro" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Exercise After a Heart Attack: The Basics</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          If you've had a heart attack, the idea of exercising again can feel terrifying. Your heart just failed you — why would you push it harder?
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          Here's the counterintuitive truth: exercise after a heart attack is not just safe when done correctly — it's one of the most important things you can do for your recovery and long-term survival. The research on this is overwhelming and consistent.
        </p>
        <p className="text-gray-300 leading-relaxed">
          This guide will walk you through the safe, evidence-based approach to exercise after a heart attack for men over 60 — what to do, when to do it, and what to watch out for.
        </p>
      </section>

      <section id="why-exercise" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Why Exercise Is Critical After a Heart Attack</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          The <a href="https://www.heart.org/en/healthy-living/fitness/fitness-basics/why-is-physical-activity-so-important-for-health-and-wellbeing" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:text-[#d9bc82] underline">American Heart Association</a> and the <a href="https://www.mayoclinic.org/diseases-conditions/heart-attack/in-depth/heart-attack-recovery/art-20048681" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:text-[#d9bc82] underline">Mayo Clinic</a> both strongly recommend exercise as a core component of heart attack recovery. Here's why:
        </p>
        <ul className="space-y-3 mb-4">
          {[
            { title: 'Strengthens the heart muscle', desc: 'Regular aerobic exercise improves cardiac output and efficiency, helping your heart pump blood more effectively.' },
            { title: 'Reduces risk of a second heart attack', desc: 'Studies show cardiac rehabilitation reduces the risk of another heart attack by 20-30% and improves survival rates significantly.' },
            { title: 'Lowers blood pressure and cholesterol', desc: 'Both are major risk factors for heart disease. Exercise addresses both without additional medication in many cases.' },
            { title: 'Improves mental health', desc: 'Depression and anxiety are extremely common after a heart attack. Exercise is one of the most effective treatments for both.' },
            { title: 'Helps with weight management', desc: 'Excess weight strains the heart. Exercise combined with appropriate nutrition helps achieve and maintain a healthy weight.' },
          ].map((item) => (
            <li key={item.title} className="flex items-start gap-3">
              <span className="text-[#c8a96e] mt-1 flex-shrink-0"><i className="ri-heart-pulse-line" /></span>
              <div>
                <strong className="text-white">{item.title}:</strong>
                <span className="text-gray-400 text-sm ml-1">{item.desc}</span>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section id="cardiac-rehab" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Cardiac Rehabilitation: Your First Step</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          Before you do anything else, ask your cardiologist about cardiac rehabilitation. This is a medically supervised program specifically designed for heart attack survivors, and it's the safest way to return to exercise.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          Cardiac rehab typically includes:
        </p>
        <ul className="space-y-2 mb-4">
          {[
            'Monitored exercise sessions with ECG monitoring',
            'Education on heart-healthy lifestyle changes',
            'Nutritional counseling',
            'Stress management techniques',
            'Medication management guidance',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="text-[#c8a96e] mt-1 flex-shrink-0"><i className="ri-checkbox-circle-line" /></span>
              <span className="text-gray-300 text-sm">{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-gray-300 leading-relaxed mb-4">
          Research consistently shows that men who complete cardiac rehabilitation have significantly better outcomes than those who don't. Yet only about 20-30% of eligible patients actually attend. Don't be in the majority on this one.
        </p>
        <p className="text-gray-300 leading-relaxed">
          After completing cardiac rehab, you'll have a much clearer picture of your exercise capacity and can begin a more independent program — like the joint-safe strength training in the <a href="https://masonhalefit.com" className="text-[#c8a96e] hover:text-[#d9bc82] underline font-semibold">Over-60 Strength Blueprint</a>.
        </p>
      </section>

      <section id="timeline" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">When Can You Start Exercising?</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          The timeline varies based on the severity of your heart attack and your individual recovery. Here's a general framework — but always defer to your cardiologist:
        </p>
        <div className="space-y-3 mb-4">
          {[
            { period: 'Days 1-7', title: 'Hospital/Early Recovery', desc: 'Light walking in the hospital or at home. Short distances, slow pace. Rest is the priority.' },
            { period: 'Weeks 1-2', title: 'Supervised Walking', desc: 'Gradual increase in walking distance and duration. Cardiac rehab typically begins. No resistance training yet.' },
            { period: 'Weeks 2-6', title: 'Cardiac Rehab Phase', desc: 'Supervised exercise sessions. Light aerobic activity. Introduction of very light resistance training under supervision.' },
            { period: 'Months 2-3', title: 'Expanding Activity', desc: 'Increasing exercise duration and intensity based on tolerance. Light independent exercise may begin with cardiologist approval.' },
            { period: 'Month 3+', title: 'Independent Exercise', desc: 'With medical clearance, begin a structured independent exercise program. Progressive resistance training can be introduced carefully.' },
          ].map((item) => (
            <div key={item.period} className="flex items-start gap-4 bg-[#111111] border border-white/10 rounded-lg p-4">
              <div className="flex-shrink-0 text-center min-w-[80px]">
                <span className="text-[#c8a96e] text-xs font-bold block">{item.period}</span>
                <span className="text-white text-sm font-black">{item.title}</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="safe-exercises" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Safe Exercises After a Heart Attack</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          Once you have medical clearance, these exercises are generally considered safe and beneficial for heart attack survivors:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {[
            { exercise: 'Walking', desc: 'The gold standard. Start slow, build gradually. Aim for 30 minutes most days.' },
            { exercise: 'Light cycling', desc: 'Stationary bike is ideal — controlled intensity, no balance risk.' },
            { exercise: 'Swimming', desc: 'Excellent low-impact cardio. Check with your doctor about pool temperature.' },
            { exercise: 'Light resistance training', desc: 'After cardiac rehab clearance. Start very light — 40-50% of max effort.' },
            { exercise: 'Yoga/stretching', desc: 'Improves flexibility and reduces stress. Avoid inverted poses initially.' },
            { exercise: 'Tai chi', desc: 'Gentle movement, balance, and stress reduction. Excellent for heart health.' },
          ].map((item) => (
            <div key={item.exercise} className="bg-[#111111] border border-white/10 rounded-lg p-4">
              <p className="text-white text-sm font-bold mb-1">{item.exercise}</p>
              <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="exercises-avoid" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Exercises to Avoid (At First)</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          In the early recovery phase, avoid these until your cardiologist clears you:
        </p>
        <ul className="space-y-2 mb-4">
          {[
            'High-intensity interval training (HIIT)',
            'Heavy weightlifting or maximal effort exercises',
            'Exercises involving breath-holding (Valsalva maneuver)',
            'Competitive sports with unpredictable intensity spikes',
            'Exercise in extreme heat or cold',
            'Any activity that causes chest pain, severe shortness of breath, or dizziness',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="text-red-400 mt-1 flex-shrink-0"><i className="ri-close-circle-line" /></span>
              <span className="text-gray-300 text-sm">{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-gray-300 leading-relaxed">
          Many of these restrictions are temporary. As your heart heals and your fitness improves, your cardiologist will gradually expand what's safe for you.
        </p>
      </section>

      <section id="warning-signs" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Warning Signs to Watch For</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          Stop exercising immediately and seek medical attention if you experience:
        </p>
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-5 mb-4">
          <ul className="space-y-2">
            {[
              'Chest pain, pressure, or tightness',
              'Severe shortness of breath disproportionate to effort',
              'Dizziness, lightheadedness, or fainting',
              'Irregular or very rapid heartbeat',
              'Unusual fatigue or weakness',
              'Pain radiating to arm, jaw, or back',
              'Nausea or cold sweats during exercise',
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="text-red-400 mt-1 flex-shrink-0"><i className="ri-alert-line" /></span>
                <span className="text-red-200/80 text-sm">{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="text-gray-300 leading-relaxed">
          Some shortness of breath and elevated heart rate during exercise is normal and expected. The key is knowing your baseline and recognizing when something feels different or wrong.
        </p>
      </section>

      <section id="long-term" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Building a Long-Term Exercise Habit</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          The goal isn't just to recover from your heart attack — it's to build a sustainable exercise habit that protects your heart for the rest of your life.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          The <a href="https://www.nia.nih.gov/health/heart-health/heart-health-and-aging" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:text-[#d9bc82] underline">National Institute on Aging</a> recommends that older adults aim for at least 150 minutes of moderate-intensity aerobic activity per week, plus muscle-strengthening activities on 2 or more days. For heart attack survivors, this is a long-term target — not a starting point.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          Once your cardiologist clears you for independent exercise, a structured program like the <a href="https://masonhalefit.com" className="text-[#c8a96e] hover:text-[#d9bc82] underline font-semibold">Over-60 Strength Blueprint</a> provides the joint-safe, progressive approach that's appropriate for men over 60 with health considerations. Always share any new program with your cardiologist before starting.
        </p>
      </section>

      <section id="faq" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-6">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {[
            { q: 'When can I exercise after a heart attack?', a: 'Most patients can begin supervised cardiac rehabilitation within 1-2 weeks of a heart attack, and light walking even sooner. The timeline depends on the severity of the event and your cardiologist\'s assessment. Never start without medical clearance.' },
            { q: 'Is strength training safe after a heart attack?', a: 'Yes, with proper medical clearance and appropriate progression. Light to moderate resistance training is actually recommended as part of cardiac rehabilitation. Start very light and progress gradually under medical supervision.' },
            { q: 'What exercises should I avoid after a heart attack?', a: 'In the early recovery phase, avoid high-intensity exercise, heavy lifting, exercises that involve breath-holding, and activities that cause chest pain or significant shortness of breath.' },
            { q: 'Can exercise prevent another heart attack?', a: 'Yes. Regular exercise is one of the most powerful tools for secondary prevention. Studies show that cardiac rehabilitation and regular exercise reduce the risk of a second heart attack by 20-30%.' },
          ].map((item) => (
            <div key={item.q} className="bg-[#111111] border border-white/10 rounded-lg p-5">
              <p className="text-white font-bold mb-2">{item.q}</p>
              <p className="text-gray-400 text-sm leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="bg-[#c8a96e]/10 border border-[#c8a96e]/30 rounded-lg p-6 mb-10 text-center">
        <p className="text-[#c8a96e] font-black text-lg mb-2">Ready to Build Back Your Strength Safely?</p>
        <p className="text-gray-300 text-sm mb-4">The Over-60 Strength Blueprint is a joint-safe, progressive strength program built for men over 60. Always get your cardiologist's clearance before starting any new program.</p>
        <a href="https://masonhalefit.com" className="inline-block bg-[#c8a96e] text-black font-black text-sm tracking-[0.15em] uppercase px-8 py-3 rounded-sm hover:bg-[#d9bc82] transition-all cursor-pointer whitespace-nowrap">
          Get The Blueprint — $19.99
        </a>
      </div>

      <AuthorBio />
      <RelatedArticles articles={relatedArticles} />
    </BlogLayout>
  );
}
